/**
 * Navigation Configuration
 * Fuente unica de verdad para la navegacion del sistema.
 * Controla secciones del sidebar, items del AppBar, y permisos RBAC.
 */
import { PERMISSIONS } from "../context/PermissionsContext/PermissionsContext";
import { PLAYGROUND_ENABLED } from "../modules/playground/common/constants";
import { CODING_OPS_V2_ENABLED } from "../modules/coding-opportunities-v2/common/constants";
import { MEMBERSHIP_ENABLED } from "../modules/membership/common/constants";
import { CASH_ENABLED } from "../modules/cash/common/constants";
import { CLINICAL_NOTE_AI_ENABLED } from "../modules/clinical-note-ai/common/constants";
import { TRANSCRIPTOR_ENABLED } from "../modules/clinical-notes-transcriptor/common/constants";
import { CDCI_V2_ENABLED } from "../modules/cdci-v2/common/constants";
import { PATIENT_PROFILES_ENABLED } from "../modules/care-management-system/common/constants";
import { TEMPLATE_CONFIRM_ENABLED } from "../modules/clinical-note-template-confirm/common/constants";

// BillingV2 (Etapa 2) — todos sus módulos están EN TEST (badge "Test", bajo
// revisión de información y confirmación) con fake data. NO deben verse en
// producción. El gate es el
// entorno, igual que PLAYGROUND_ENABLED y CODING_OPS_V2_ENABLED:
//   - `yarn start`  → NODE_ENV = "development" → BILLING_V2_ENABLED = true
//   - `yarn build`  → NODE_ENV = "production"  → BILLING_V2_ENABLED = false
// En el bundle de producción la sección entera ni siquiera se registra en el
// sidebar. Cuando un módulo pase a productivo, sacarlo de esta sección.
const BILLING_V2_ENABLED = process.env.NODE_ENV === "development";

// Pre-Visit Planning: módulo PRODUCTIVO en piloto. Vive en el grupo Visit
// Cycle de la sección Billing V2 (Stage 2), visible en dev y prod por igual —
// la sección se monta siempre; lo DEV-ONLY son los demás módulos del paquete.
// ACCESO: solo el RBAC — módulo `previsit_planning` (25) del catálogo, mapeado
// en app/AccessDenied/roleRoutePermissions.js. Decide quién ve el ítem y quién
// pasa el RoleGuard; se administra en Access Control, sin deploy.
//
// El 2026-08-31 se le quitaron los dos parches que tenía encima:
// `hiddenForRoleKeys: ["cca"]` (cubría el hueco de no tener moduleKey) y la
// allowlist de empleados del piloto, que era un segundo gate invisible.
// Lo que sigue vigente es el alcance de datos: providerAccess.js define QUÉ
// citas ve cada médico una vez adentro.
// Split por rol (2026-09-14): Pre-Visit Planning son ahora DOS módulos con el
// mismo gate RBAC (`previsit_planning`, 25) — el original quedó como el del
// Medical Receptionist (checklist administrativo) y la réplica
// `previsit-planning-ma` es la del Medical Assistant (checklist clínico).
const PREVISIT_NAV_ITEM = {
  id: "previsit-planning",
  path: "/previsit-planning",
  labelKey: "nav.previsitPlanning",
  labelFallback: "Pre-Visit Planning MR",
  icon: "EventAvailableIcon",
  iconColor: "#00838f",
  permissions: null,
  badge: "New",
};

const PREVISIT_MA_NAV_ITEM = {
  id: "previsit-planning-ma",
  path: "/previsit-planning-ma",
  labelKey: "nav.previsitPlanningMa",
  labelFallback: "Pre-Visit Planning MA",
  icon: "EventAvailableIcon",
  iconColor: "#00695c",
  permissions: null,
  badge: "New",
};

// Navegación móvil (< md). Por ahora un solo módulo está preparado para
// celular: el sidebar muestra únicamente los ítems con `mobile: true` y el
// login aterriza en MOBILE_HOME_PATH en vez de /Main.
// Con el transcriptor oculto de producción (TRANSCRIPTOR_ENABLED) no queda
// ningún módulo mobile: el login móvil vuelve a /Main y el sidebar móvil
// muestra la navegación completa (ver HAS_MOBILE_ITEMS en NavigationSections).
export const MOBILE_HOME_PATH = TRANSCRIPTOR_ENABLED
  ? "/clinical-notes-transcriptor"
  : "/Main";

// Iconos referenciados por string para importar en componentes
export const SIDEBAR_SECTIONS = [
  {
    id: "clinical",
    labelKey: "nav.clinical",
    labelFallback: "Clinical",
    icon: "LocalHospitalIcon",
    permissions: [
      PERMISSIONS.CAREGAPS_READ,
      PERMISSIONS.CODING_READ,
      PERMISSIONS.AI_USE,
      PERMISSIONS.NOTES_READ,
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.REFERRALS_READ,
      PERMISSIONS.LAB_READ,
      PERMISSIONS.ASSIGNMENTS_WRITE,
    ],
    items: [
      {
        id: "care-gaps",
        path: "/care-gaps",
        labelKey: "nav.careGaps",
        labelFallback: "Care Gaps",
        icon: "BarChartIcon",
        iconColor: "#1976d2",
        permissions: [PERMISSIONS.CAREGAPS_READ],
      },
      // Care Gaps (legacy) — oculto del sidebar. El módulo nuevo en
      // /care-gaps es la versión canónica. El legacy sigue accesible solo
      // por URL directa (/care-gaps-legacy) si se necesita revisión puntual.
      {
        id: "coding-opportunities",
        path: "/coding-opportunities",
        labelKey: "nav.codingOpportunities",
        labelFallback: "Coding Opportunities",
        icon: "CodeIcon",
        iconColor: "#ff9800",
        permissions: [PERMISSIONS.CODING_READ],
      },
      // Coding Opportunities V2 — EN DESARROLLO. Solo aparece en el sidebar
      // cuando NODE_ENV === "development" (CODING_OPS_V2_ENABLED). En el build
      // de producción el ítem ni siquiera se registra.
      ...(CODING_OPS_V2_ENABLED
        ? [
            {
              id: "coding-opportunities-v2",
              path: "/coding-opportunities-v2",
              labelKey: "nav.codingOpportunitiesV2",
              labelFallback: "Coding Opportunities V2",
              icon: "InsightsIcon",
              iconColor: "#0F6E6E",
              permissions: [PERMISSIONS.CODING_READ],
              badge: "New",
            },
          ]
        : []),
      // AI Clinical Notes — DEV-ONLY desde 2026-08-31. El módulo tampoco
      // publica su ruta en producción (clinical-note-ai/common/constants.js),
      // así que fuera de desarrollo no existe ni por URL directa.
      ...(CLINICAL_NOTE_AI_ENABLED
        ? [
            {
              id: "ai-clinical-notes",
              path: "/ClinicalNoteAi",
              labelKey: "nav.aiClinicalNotes",
              labelFallback: "AI Clinical Notes",
              icon: "FactCheckIcon",
              iconColor: "#1565c0",
              permissions: [PERMISSIONS.AI_USE],
            },
          ]
        : []),
      // Clinical Notes Transcriptor — módulo MOBILE-FIRST. `mobile: true` lo
      // marca como el único ítem visible cuando la app se abre desde un
      // celular (< md); ver NavigationSections y MOBILE_HOME_PATH. En desktop
      // se lista acá, en Clinical, como cualquier otro módulo.
      // OCULTO DE PRODUCCIÓN (2026-09-14, TRANSCRIPTOR_ENABLED) mientras se
      // revisan los fallos de transcripción con audios largos.
      ...(TRANSCRIPTOR_ENABLED
        ? [
            {
              id: "clinical-notes-transcriptor",
              path: "/clinical-notes-transcriptor",
              labelKey: "nav.clinicalNotesTranscriptor",
              labelFallback: "Clinical Notes Transcriptor",
              icon: "HistoryEduIcon",
              iconColor: "#0F6E6E",
              permissions: null,
              badge: "New",
              mobile: true,
            },
          ]
        : []),
      // {
      //   id: "clinical-notes",
      //   path: "/ClinicalNotesList",
      //   labelKey: "nav.clinicalNotes",
      //   labelFallback: "Clinical Notes",
      //   icon: "DescriptionIcon",
      //   iconColor: "#43a047",
      //   permissions: [PERMISSIONS.NOTES_READ],
      // },
      // Clinical Notes Audit vivió acá hasta 2026-10-14: se movió a la sección
      // Audit (abajo) junto con Audit Rules. La ruta y el RBAC no cambiaron.
      {
        id: "medication-reconciliation",
        path: "/MedicationReconciliationPage",
        labelKey: "nav.medicationReconciliation",
        labelFallback: "Medication Reconciliation",
        icon: "MedicationIcon",
        iconColor: "#be07e2",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "specialist-referrals",
        path: "/specialist-referrals",
        labelKey: "nav.specialistReferrals",
        labelFallback: "Specialist Referrals",
        icon: "LocalHospitalIcon",
        iconColor: "#7986cb",
        permissions: [PERMISSIONS.REFERRALS_READ],
      },
      {
        id: "lab-phlebotomy",
        path: "/LabPhlebotomy",
        labelKey: "nav.labPhlebotomy",
        labelFallback: "Lab Phlebotomy",
        icon: "ScienceIcon",
        iconColor: "#4db6ac",
        permissions: [PERMISSIONS.LAB_READ],
      },
      {
        id: "assignments",
        path: "/assignments",
        labelKey: "nav.assignments",
        labelFallback: "Assignments",
        icon: "PersonIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.ASSIGNMENTS_WRITE],
      },
      // Patient Geography (api/geography) — gobernado por el módulo
      // `patient_geography` (38) del catálogo desde 2026-08-31. Ya no lleva
      // `hiddenForRoleKeys: ["cca"]`: ese parche cubría el hueco de no tener
      // moduleKey, y ahora quién lo ve se decide en la matriz RBAC.
      {
        id: "patient-geography",
        path: "/patient-geography",
        labelKey: "nav.patientGeography",
        labelFallback: "Patient Geography",
        icon: "TravelExploreIcon",
        iconColor: "#0F6E6E",
        permissions: [PERMISSIONS.PATIENTS_READ],
        badge: "New",
      },
    ],
  },
  // Audit — auditoría de notas clínicas. La sección se monta siempre y cada
  // ítem resuelve su propio gate vía canUserAccessRoute: ambos módulos los
  // decide ROUTE_ALLOWED_EMPLOYEE_IDS con adminBypass (desde 2026-10-20 —
  // Clinical Notes Audit: empleados 68/89/90/74/80 + admin/CEO; Audit Rules:
  // admin/CEO + employee 80). Al crearse los módulos en TAB_MODULES decide la
  // matriz RBAC (§4.4 de la guía de reglas separa además usar el auditor de
  // administrar reglas).
  {
    id: "audit",
    labelKey: "nav.audit",
    labelFallback: "Audit",
    icon: "FactCheckIcon",
    permissions: null,
    items: [
      {
        id: "clinical-notes-audit",
        path: "/clinical-notes-audit",
        labelKey: "nav.clinicalNotesAudit",
        labelFallback: "Clinical Notes Audit",
        icon: "FactCheckIcon",
        iconColor: "#8d6e63",
        permissions: null,
        badge: "New",
      },
      {
        id: "audit-rules",
        path: "/audit-rules",
        labelKey: "nav.auditRules",
        labelFallback: "Audit Rules",
        icon: "GavelIcon",
        iconColor: "#6a1b9a",
        permissions: null,
        badge: "New",
      },
    ],
  },
  {
    id: "care-management",
    labelKey: "nav.careManagement",
    labelFallback: "Care Management",
    icon: "FavoriteBorderIcon",
    permissions: [
      PERMISSIONS.PATIENTS_READ,
      PERMISSIONS.CAREPLANS_READ,
    ],
    items: [
      {
        id: "discharged",
        path: "/clinical-info/discharged",
        labelKey: "nav.discharged",
        labelFallback: "Discharged",
        icon: "ExitToAppIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "admitted",
        path: "/clinical-info/admitted",
        labelKey: "nav.admitted",
        labelFallback: "Admitted",
        icon: "LocalHospitalIcon",
        iconColor: "#42a5f5",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "emergency",
        path: "/clinical-info/emergency",
        labelKey: "nav.emergency",
        labelFallback: "Emergency",
        icon: "EmergencyIcon",
        iconColor: "#ff7043",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      // High Cost — módulo independiente (/high-cost). Reemplaza al viejo de
      // clinical-info (/clinical-info/high-cost ahora redirige aquí).
      {
        id: "high-cost",
        path: "/high-cost",
        labelKey: "nav.highCost",
        labelFallback: "High Cost",
        icon: "AttachMoneyIcon",
        iconColor: "#66bb6a",
        permissions: [PERMISSIONS.PATIENTS_READ],
        // Chip que indica que el módulo volvió (estaba en mantenimiento).
        badge: "New",
      },
      // AMD Coverage (módulo 41): relación Availity ↔ AdvancedMD + link.
      // Vive junto a High Cost porque comparten el flujo de vinculación.
      // Quién lo ve lo decide la matriz RBAC vía la key `amd_coverage`.
      {
        id: "amd-coverage",
        path: "/amd-coverage",
        labelKey: "nav.amdCoverage",
        labelFallback: "AMD Coverage",
        icon: "LinkIcon",
        iconColor: "#26a69a",
        permissions: [PERMISSIONS.PATIENTS_READ],
        badge: "New",
      },
      {
        id: "high-risk",
        path: "/clinical-info/high-risk",
        labelKey: "nav.highRisk",
        labelFallback: "High Risk",
        icon: "WarningIcon",
        iconColor: "#ffa726",
        permissions: [PERMISSIONS.PATIENTS_READ],
      },
      {
        id: "no-recent-visits",
        path: "/no-recent-visits",
        labelKey: "nav.noRecentVisits",
        labelFallback: "No Recent Visits",
        icon: "VisibilityOffIcon",
        iconColor: "#e57373",
        permissions: [PERMISSIONS.PATIENTS_READ],
        badge: "New",
      },
      {
        id: "care-plans",
        path: "/care-plan",
        labelKey: "nav.carePlans",
        labelFallback: "Care Plans",
        icon: "AssignmentIcon",
        iconColor: "#26a69a",
        permissions: [PERMISSIONS.CAREPLANS_READ],
      },
      // Patient Profiles — DEV-ONLY. El ítem ya estaba oculto en producción,
      // pero la RUTA se montaba igual y se llegaba por URL directa; desde
      // 2026-08-31 el módulo tampoco la publica fuera de desarrollo
      // (care-management-system/common/constants.js). La constante es la misma
      // de los dos lados para que no puedan desincronizarse.
      ...(PATIENT_PROFILES_ENABLED
        ? [
            {
              id: "patient-profiles",
              path: "/patient-profiles",
              labelKey: "nav.patientProfiles",
              labelFallback: "Patient Profiles",
              icon: "AssignmentIndIcon",
              iconColor: "#8e24aa",
              permissions: [PERMISSIONS.PATIENTS_WRITE],
            },
          ]
        : []),
    ],
  },
  // Contact Center — la sección se monta SIEMPRE (dev y prod). La visibilidad
  // por usuario la resuelve el sidebar con user.modules (RBAC) para los módulos
  // registrados en Administration → Access Control. Inactives queda activo y
  // visible a usuarios autenticados mientras backend crea su moduleKey.
  {
    id: "contact-center",
    labelKey: "nav.contactCenter",
    labelFallback: "Contact Center",
    icon: "SupportAgentIcon",
    permissions: null,
    items: [
      {
        id: "call-center-intake",
        path: "/call-center-intake",
        labelKey: "nav.callCenterIntake",
        labelFallback: "Call Center Intake",
        icon: "PhoneInTalkIcon",
        iconColor: "#5C6BC0",
        permissions: null,
        badge: "New",
      },
      // Transportation OCULTO (2026-08-10): sin contrato por ahora, no se está
      // trabajando en él. El módulo exporta [] (flag en su index.js); al
      // retomarse, restaurar este ítem:
      // { id: "transportation", path: "/transportation",
      //   labelKey: "nav.transportation", labelFallback: "Transportation",
      //   icon: "DirectionsBusIcon", iconColor: "#26a69a",
      //   permissions: null, badge: "New" },
      {
        id: "new-members",
        path: "/new-members",
        labelKey: "nav.newMembers",
        labelFallback: "New Members",
        icon: "PersonAddIcon",
        iconColor: "#ef6c00",
        permissions: null,
        badge: "New",
      },
      {
        id: "inactives",
        path: "/inactives",
        labelKey: "nav.inactives",
        labelFallback: "Inactives",
        icon: "PersonOffIcon",
        iconColor: "#d97706",
        permissions: null,
        badge: "New",
      },
      // No Recent Visit (call center) — REACTIVADO EN PRODUCCIÓN (2026-10-01):
      // se retiró el gate por entorno del 2026-09-30. Visibilidad por RBAC
      // (hereda call_center), como el resto de la sección.
      {
        id: "call-recent-visit",
        path: "/call-recent-visit",
        labelKey: "nav.callRecentVisit",
        labelFallback: "No Recent Visit",
        icon: "EventBusyIcon",
        iconColor: "#c2185b",
        permissions: null,
        badge: "New",
      },
      {
        id: "welcome-oep",
        path: "/welcome-oep",
        labelKey: "nav.welcomeOep",
        labelFallback: "Welcome & OEP",
        icon: "CampaignIcon",
        iconColor: "#7e57c2",
        permissions: null,
        badge: "New",
      },
      // Rediseñado 2026-08-10: gestor de asignaciones paciente → agente. La
      // visibilidad la gatea el moduleKey call_assignments (módulo 30 del
      // catálogo) vía canUserAccessRoute, igual que el resto de la sección.
      {
        id: "call-assignments",
        path: "/call-assignments",
        labelKey: "nav.callAssignments",
        labelFallback: "Call Assignments",
        icon: "AddIcCallIcon",
        iconColor: "#43a047",
        permissions: null,
        badge: "New",
      },
    ],
  },
  // Membership — EN DESARROLLO. La sección entera se monta solo cuando
  // MEMBERSHIP_ENABLED (NODE_ENV === "development"); en producción este spread
  // queda vacío y no aparece en el sidebar. El gate es el entorno, no el RBAC
  // (el módulo todavía no tiene moduleKey en el catálogo de Access Control).
  ...(MEMBERSHIP_ENABLED
    ? [
        {
          id: "membership",
          labelKey: "nav.membership",
          labelFallback: "Membership",
          icon: "CardMembershipIcon",
          permissions: null,
          // El padrón va primero: es la entrada natural a la sección. Las otras
          // tres son la misma información mirada de forma transversal, para el
          // trabajo que cruza miembros.
          items: [
            {
              id: "membership-members",
              path: "/membership/members",
              labelKey: "nav.members",
              labelFallback: "Members",
              icon: "CardMembershipIcon",
              iconColor: "#A6761D",
              permissions: null,
              badge: "Develop",
            },
            {
              id: "membership-contacts",
              path: "/membership/contacts",
              labelKey: "nav.contacts",
              labelFallback: "Contacts",
              icon: "ContactPhoneIcon",
              iconColor: "#5C6BC0",
              permissions: null,
              badge: "Develop",
            },
            {
              id: "membership-payments",
              path: "/membership/payments",
              labelKey: "nav.payments",
              labelFallback: "Payments",
              icon: "PaymentsIcon",
              iconColor: "#1F8A5B",
              permissions: null,
              badge: "Develop",
            },
            {
              id: "membership-payment-methods",
              path: "/membership/payment-methods",
              labelKey: "nav.paymentMethods",
              labelFallback: "Payment Methods",
              icon: "CreditCardIcon",
              iconColor: "#A6761D",
              permissions: null,
              badge: "Develop",
            },
            {
              id: "membership-documents",
              path: "/membership/documents",
              labelKey: "nav.documents",
              labelFallback: "Documents & Consents",
              icon: "HistoryEduIcon",
              iconColor: "#7e57c2",
              permissions: null,
              badge: "Develop",
            },
          ],
        },
      ]
    : []),
  // Cash — EN DESARROLLO (source mock, sin contrato backend). Mismo gate que
  // Membership: solo se monta con CASH_ENABLED (NODE_ENV === "development").
  // Sin moduleKey todavía: sin mapping RBAC hasta que backend lo registre.
  ...(CASH_ENABLED
    ? [
        {
          id: "cash",
          labelKey: "nav.cash",
          labelFallback: "Cash",
          icon: "AttachMoneyIcon",
          permissions: null,
          items: [
            {
              id: "cash-dashboard",
              path: "/cash/dashboard",
              labelKey: "nav.cashDashboard",
              labelFallback: "Dashboard",
              icon: "BarChartIcon",
              iconColor: "#0F766E",
              permissions: null,
              badge: "Develop",
            },
          ],
        },
      ]
    : []),
  // ===========================================================================
  // Etapa 2 — BillingV2 (docs/modulos/BillingV2). Módulos en test (badge "Test",
  // bajo revisión de información y confirmación): funcionan con fake data
  // alineada al contrato real hasta que el backend exponga sus endpoints.
  // Coding Opportunities (Módulo E) ya es productivo y vive en la sección
  // Clinical.
  //
  // La sección se monta SIEMPRE: contiene Pre-Visit Planning (productivo, en
  // el grupo Visit Cycle) y Charge Capture (productivo desde 2026-09-16, solo
  // admin/CEO vía RBAC). El resto de módulos del paquete y la guía siguen
  // siendo DEV-ONLY (BILLING_V2_ENABLED) — en producción la sección solo
  // muestra lo productivo.
  // ===========================================================================
  {
    id: "billing-v2",
    labelKey: "nav.billingV2",
    labelFallback: "Billing V2 (Stage 2)",
    icon: "ReceiptLongIcon",
    permissions: null,
    // Items agrupados con subheaders ({ type: "subheader" }) replicando los
    // 5 grupos del Stage 2 Guide (src/modules/stage2-guide). El orden es el
    // orden recomendado de uso que documenta la guía.
    items: [
      ...(BILLING_V2_ENABLED
        ? [
            {
              id: "stage2-guide",
              path: "/stage2-guide",
              labelKey: "nav.stage2Guide",
              labelFallback: "Stage 2 Guide",
              icon: "MenuBookIcon",
              iconColor: "#0F6E6E",
              permissions: null,
            },
          ]
        : []),
      {
        type: "subheader",
        id: "bv2-group-visit-cycle",
        labelKey: "nav.bv2GroupVisitCycle",
        labelFallback: "Visit Cycle",
      },
      PREVISIT_NAV_ITEM,
      PREVISIT_MA_NAV_ITEM,
      // CDCI se monta SIEMPRE (dev y prod). Desde 2026-08-31 dejó de ser un
      // piloto por lista de empleados: tiene módulo propio en el catálogo
      // (`cdci`, 37) y quién lo ve se decide en la matriz RBAC, como el resto.
      // Por eso ya no lleva `alwaysUseRouteGate` — el preview "view as" de
      // admin vuelve a funcionar igual que en cualquier otro módulo.
      {
        id: "cdci",
        path: "/cdci",
        labelKey: "nav.cdci",
        labelFallback: "CDCI — Provider Workspace",
        icon: "EventNoteIcon",
        iconColor: "#0F6E6E",
        permissions: null,
        badge: "New",
      },
      // CDCI v2 — banco de trabajo del refactor del transcriptor (AI Scribe,
      // docs/modulos/AIScribe/). Solo dev (CDCI_V2_ENABLED): reusa todo del v1
      // salvo la capa de transcripción, que está duplicada para poder cambiarla.
      ...(CDCI_V2_ENABLED
        ? [
            {
              id: "cdci-v2",
              path: "/cdci-v2",
              labelKey: "nav.cdciV2",
              labelFallback: "CDCI v2 — AI Scribe (dev)",
              icon: "EventNoteIcon",
              iconColor: "#7B5EA7",
              permissions: null,
              badge: "Develop",
            },
          ]
        : []),
      // TEMPORAL — pantalla de revisión de los templates de nota clínica.
      // La revisión TERMINÓ (2026-09-25): RETIRADO de producción — el ítem
      // solo se monta en dev (TEMPLATE_CONFIRM_ENABLED, gate por entorno,
      // como Playground). Borrar la entrada al eliminar el módulo.
      ...(TEMPLATE_CONFIRM_ENABLED
        ? [
            {
              id: "clinical-note-template-confirm",
              path: "/clinical-note-template-confirm",
              labelKey: "nav.clinicalNoteTemplateConfirm",
              labelFallback: "Clinical Note — Template Confirm",
              icon: "FactCheckIcon",
              iconColor: "#B5760A",
              permissions: null,
              badge: "Develop",
            },
          ]
        : []),
      // Charge Capture se monta SIEMPRE (dev y prod) desde 2026-09-16, pero en
      // producción SOLO lo ven admin/CEO: la ruta está mapeada a la key
      // pendiente `charge_capture` (roleRoutePermissions.js) y ese mismo gate
      // decide la visibilidad del ítem — para cualquier otro rol el ítem no
      // aparece y la URL directa cae en Access Denied.
      {
        id: "charge-capture",
        path: "/charge-capture",
        labelKey: "nav.chargeCapture",
        labelFallback: "Charge Capture",
        icon: "ReceiptLongIcon",
        iconColor: "#7e57c2",
        permissions: null,
        badge: "New",
      },
      ...(BILLING_V2_ENABLED
        ? [
      {
        id: "denials-management",
        path: "/denials-management",
        labelKey: "nav.denialsManagement",
        labelFallback: "Denials Management",
        icon: "GavelIcon",
        iconColor: "#B5760A",
        permissions: null,
        badge: "New",
      },
      {
        type: "subheader",
        id: "bv2-group-population",
        labelKey: "nav.bv2GroupPopulation",
        labelFallback: "Population Care",
      },
      {
        id: "post-discharge",
        path: "/post-discharge",
        labelKey: "nav.postDischarge",
        labelFallback: "Post-Discharge",
        icon: "ExitToAppIcon",
        iconColor: "#1B5FA8",
        permissions: null,
        badge: "Test",
      },
      {
        id: "high-risk-surveillance",
        path: "/high-risk-surveillance",
        labelKey: "nav.highRiskSurveillance",
        labelFallback: "High Risk Surveillance",
        icon: "MonitorHeartIcon",
        iconColor: "#C0392B",
        permissions: null,
        badge: "Test",
      },
      {
        id: "medication-adherence",
        path: "/medication-adherence",
        labelKey: "nav.medicationAdherence",
        labelFallback: "Medication Adherence",
        icon: "MedicationIcon",
        iconColor: "#2e7d32",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-growth",
        labelKey: "nav.bv2GroupGrowth",
        labelFallback: "Membership & Outreach",
      },
      {
        id: "member-outreach",
        path: "/member-outreach",
        labelKey: "nav.memberOutreach",
        labelFallback: "Member Outreach",
        icon: "CampaignIcon",
        iconColor: "#ef6c00",
        permissions: null,
        badge: "Test",
      },
      {
        id: "member-onboarding",
        path: "/member-onboarding",
        labelKey: "nav.memberOnboarding",
        labelFallback: "New Member Onboarding",
        icon: "PersonAddIcon",
        iconColor: "#00695c",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-provider",
        labelKey: "nav.bv2GroupProvider",
        labelFallback: "Provider Improvement",
      },
      {
        id: "provider-doc-coach",
        path: "/provider-doc-coach",
        labelKey: "nav.providerDocCoach",
        labelFallback: "Provider Doc Coach",
        icon: "SchoolIcon",
        iconColor: "#6a1b9a",
        permissions: null,
        badge: "Test",
      },
      {
        id: "provider-performance",
        path: "/provider-performance",
        labelKey: "nav.providerPerformance",
        labelFallback: "Provider Performance",
        icon: "LeaderboardIcon",
        iconColor: "#5C6BC0",
        permissions: null,
        badge: "Test",
      },
      {
        type: "subheader",
        id: "bv2-group-dashboards",
        labelKey: "nav.bv2GroupDashboards",
        labelFallback: "Executive Dashboards",
      },
      {
        id: "membership-analytics",
        path: "/membership-analytics",
        labelKey: "nav.membershipAnalytics",
        labelFallback: "Membership Analytics",
        icon: "GroupsIcon",
        iconColor: "#3949ab",
        permissions: null,
        badge: "Test",
      },
      {
        id: "clinical-operations",
        path: "/clinical-operations",
        labelKey: "nav.clinicalOperations",
        labelFallback: "Clinical Operations",
        icon: "TimelineIcon",
        iconColor: "#0277bd",
        permissions: null,
        badge: "Test",
      },
      {
        id: "executive-financial",
        path: "/executive-financial",
        labelKey: "nav.executiveFinancial",
        labelFallback: "Executive Financial",
        icon: "AttachMoneyIcon",
        iconColor: "#1F8A5B",
        permissions: null,
        badge: "Test",
      },
      {
        id: "billing-dashboard",
        path: "/billing-dashboard",
        labelKey: "nav.billingDashboard",
        labelFallback: "Billing Dashboard",
        icon: "ReceiptLongIcon",
        iconColor: "#0F6E6E",
        permissions: null,
        badge: "Test",
      },
          ]
        : []),
    ],
  },
  {
    id: "general",
    labelKey: "nav.general",
    labelFallback: "General",
    icon: "CalendarMonthIcon",
    permissions: null, // Accesible para todos los autenticados
    items: [
      {
        id: "schedule",
        path: "/general",
        labelKey: "nav.schedule",
        labelFallback: "Schedule",
        icon: "CalendarMonthIcon",
        iconColor: "#667EEA",
        permissions: null,
      },
      {
        id: "tasks",
        path: "/tasks",
        labelKey: "nav.tasks",
        labelFallback: "Tasks",
        icon: "AssignmentIcon",
        iconColor: "#6366F1",
        permissions: null,
      },
      {
        id: "technical-issues",
        path: "/technical-issues",
        labelKey: "nav.technicalIssues",
        labelFallback: "Technical Issues",
        icon: "BugReportIcon",
        iconColor: "#EF4444",
        permissions: null,
      },
    ],
  },
  {
    id: "administration",
    labelKey: "nav.administration",
    labelFallback: "Administration",
    icon: "AdminPanelSettingsIcon",
    permissions: [PERMISSIONS.ADMIN_USERS, PERMISSIONS.SUPPORT_ACCESS, PERMISSIONS.DOCUMENTS_READ],
    items: [
      {
        id: "administration",
        path: "/administration",
        labelKey: "nav.administration",
        labelFallback: "Administration",
        icon: "AdminPanelSettingsIcon",
        iconColor: "#ef5350",
        permissions: [PERMISSIONS.ADMIN_USERS],
      },
      {
        id: "library",
        path: "/library",
        labelKey: "nav.library",
        labelFallback: "Library",
        icon: "DescriptionIcon",
        iconColor: "#5C6BC0",
        permissions: [PERMISSIONS.DOCUMENTS_READ],
      },
      {
        id: "support",
        path: "/support",
        labelKey: "nav.technicalSupport",
        labelFallback: "Technical Support",
        icon: "BugReportIcon",
        iconColor: "#9e9e9e",
        permissions: [PERMISSIONS.SUPPORT_ACCESS],
      },
    ],
  },
  // Playground (DEV-ONLY) — la sección entera se monta solo cuando
  // PLAYGROUND_ENABLED (NODE_ENV === "development"). En producción este spread
  // queda vacío y no aparece en el sidebar. El gate es el entorno, no el RBAC.
  ...(PLAYGROUND_ENABLED
    ? [
        {
          id: "dev",
          labelKey: "nav.devTools",
          labelFallback: "Dev Tools",
          icon: "TerminalIcon",
          permissions: null,
          items: [
            {
              id: "playground",
              path: "/playground",
              labelKey: "nav.playground",
              labelFallback: "Playground",
              icon: "TerminalIcon",
              iconColor: "#9c27b0",
              permissions: null,
            },
          ],
        },
      ]
    : []),
];

export const APPBAR_ITEMS = [
  {
    id: "dashboard",
    path: "/main",
    labelKey: "nav.dashboard",
    labelFallback: "Dashboard",
    icon: "HomeIcon",
    permissions: null, // Accesible para todos
  },
  {
    id: "patient-map",
    path: "/patientMap",
    labelKey: "nav.patientMap",
    labelFallback: "Patient Map",
    icon: "MapIcon",
    permissions: [PERMISSIONS.PATIENTS_READ],
  },
  {
    // Patient Map del call center (solo lectura). Quién lo ve lo decide
    // canUserAccessRoute vía la allowlist de roleRoutePermissions.js: solo
    // admin/CEO, cca y care_coordinator — para el resto el tab ni se pinta.
    id: "patient-map-call-center",
    path: "/patient-map/call-center",
    labelKey: "nav.patientMapCallCenter",
    labelFallback: "Patient Map CC",
    icon: "SupportAgentIcon",
    permissions: [PERMISSIONS.PATIENTS_READ],
  },
];

export default SIDEBAR_SECTIONS;
